<template>
  <fts-collapsible-element
    immersive
    v-model="showComment"
    class="fts-comment"
    :data-comment-queue="files && files.length || 0"
    @content:visible="onFocus"
  >
    <div
      slot="toggle"
      slot-scope="{ toggle }"
      class="comment-toggle"
      @click="toggle"
    >
      Click here to add a comment... &nbsp;<i class="fts-icon-comment"></i>
    </div>
    <template #content>
      <!-- uploaded attachments -->
      <fts-file
        class="comment-file-attachments"
        :prefixURL="`${vectorId}-comment/`" 
        :data="comment.attachments" 
        editable 
        @removeFile="removeFile($event)"
      />
      <!-- comment -->
      <div class="comment-wrapper" @paste="onPaste">
        <div class="comment-content">
          <!-- queue attachments -->
          <fts-file 
            class="comment-uploader" 
            :data="files" 
            cancellable
            @cancelUpload="cancelUpload"
          />
          <!-- editor -->
          <div class="comment-editor fts-scrollbar fts-scrollbar-overlay" :style="{ maxHeight: editorMaxHeight }">      
            <editor-content
              class="editor__content fts-editor__content"
              :editor="editor"
              v-if="editor"
            />
          </div>
          <!-- actions -->
          <div class="comment-action-bar" ref="commentActionBar">
            <div class="comment-actions">
              <!-- file upload action -->
              <div class="file-upload-container icon-handle">
                <file-upload
                  ref="upload"
                  v-model="files"
                  :drop="true"
                  :multiple="true"
                  :post-action="fileUploadURL"
                  :data="{ vectorId: vectorId, fieldName: 'files' }"
                  :input-id="`paperclip${id}`"
                  @input-file="inputFile"
                  @input-filter="inputFilter"
                >
                  <font-awesome-icon
                    class="comment-action-icon"
                    :icon="['fas', 'paperclip']"
                  />
                </file-upload>
              </div>
              <!-- emoji action -->
              <div>
                <fts-emoji 
                  append-to-body 
                  :toggle-size="16" 
                  @updateEmoji="emojiOnUpdated"
                />
              </div>
              <!-- mention action -->
              <div @click="insertTag" v-if="mention">
                <font-awesome-icon
                  class="comment-action-icon"
                  :icon="['fas', 'at']"
                />
              </div>
            </div>
            <!-- send action -->
            <fts-btn 
              icon 
              text 
              sm 
              color="primary" 
              :loading="submitting"
              :disabled="!enabledSend"
              @click.native.prevent="send"
              v-if="sendAction"
            >
              <font-awesome-icon :icon="['far', 'paper-plane']" />
            </fts-btn>
          </div>
        </div>
      </div>
      <!-- mention users -->
      <div
        class="suggestion-list"
        ref="suggestions"
        v-show="showSuggestions"
        v-if="mention"
      >
        <template v-if="hasResults">
          <div
            v-for="(user, index) in filteredUsers"
            :key="`${user.userName || user.username}-${index}`"
            class="suggestion-list__item"
            :class="{ 'is-selected': navigatedUserIndex === index }"
            @click.stop="selectUser(user)"
          >
            <fts-profile :profile="user" :profile-photo-size="30">
              <template #profileName>
                <div class="user-profile-name">{{ user.displayName }}</div>
                <div class="user-profile-username">{{ user.userName || user.username }}</div>
              </template>
            </fts-profile>
          </div>
        </template>
        <div v-else class="suggestion-list__item is-empty">No users found</div>
      </div>
    <!-- </div> -->
    </template>
  </fts-collapsible-element>
</template>

<script>
import Vue from 'vue';
import tippy from '../tippy-4.3.5';
import { Editor, EditorContent, EditorMenuBar } from 'tiptap';
import {
  HardBreak,
  Bold,
  Italic,
  Placeholder,
  History,
} from 'tiptap-extensions';
import Mention from '../tiptap-extensions/Mention';
import VueUploadComponent from '../vue-upload-component/index';
Vue.component("file-upload", VueUploadComponent);
import ImageCompressor from 'compressorjs';
import Doc from '../library/doc';

import webAppConfig from '../commons/webApp.config';
import commons from '../commons/commons';
import { htmlValidator } from '../commons/validator';
import { classificationStore } from '../store/classificationStore';

import FTSBtn from './FTSBtn';
import FTSCollapsibleElement from './FTSCollapsibleElement';
import FTSEmoji from './FTSEmoji';
import FTSFile from './file/FTSFile';
import FTSProfile from './FTSProfile';

const getCommentData = (content) => {
  if (content) {
    const mentionPattern = /(<span[^>]*data-mention-id="[^"]*"[^>]*>.*?<\/span>)/g;
    const splitContent = content.split(mentionPattern).filter(x => x);
    const joinedContent = splitContent.reduce((accumulator, currentValue, currentIndex) => {
      let result = accumulator + currentValue;
      if (Boolean(currentValue.match(mentionPattern)) && currentIndex !== splitContent.length - 1) {
        const nextItem = splitContent[currentIndex + 1];
        const htmlEndTagPattern = /^<\/\s*([a-zA-Z][a-zA-Z0-9]*)\s*>/;
        if (nextItem && nextItem.charCodeAt(0) !== 32 && !htmlEndTagPattern.test(nextItem)) {
          result = result + ' ';
        }
      }
      return result;
    }, "");
    if (content !== joinedContent) {
      return joinedContent;
    }
  }
  return content;
}

export default {
  props: {
    vectorId: [String, Number],
    locusId: [String, Number],
    locusDetailId: [String, Number],
    locusCollaborationId: [String, Number],
    initialComment: Boolean,
    comment: {
      type: Object,
      default: () => ({}),
    },
    avatar: {
      type: Boolean,
      default: true,
    },
    sendAction: Boolean,
    editorMaxHeight: {
      type: String,
      default: '240px'
    },
    mention: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: 'Add a comment...'
    },
    scrollIntoViewOptions: Object
  },
  model: {
    prop: "comment",
    event: "updateComment"
  },
  components: {
    EditorContent,
    FileUpload: VueUploadComponent,
    "fts-btn": FTSBtn,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-emoji": FTSEmoji,
    "fts-file": FTSFile,
    "fts-profile": FTSProfile
  },
  data: () => ({
    id: null,
    showComment: false,
    query: null,
    suggestionRange: null,
    observer: null,
    editor: null,
    navigatedUserIndex: 0,
    submitting: false,
    files: [],
    filteredUsers: [],
    insertMention: () => {},
  }),
  computed: {
    fileUploadURL() {
      return webAppConfig.fileUploadURL;
    },
    users() {
      return classificationStore.state.users.filter(x => !x.disable);
    },
    hasResults() {
      return this.filteredUsers.length;
    },
    showSuggestions() {
      return this.query || this.hasResults;
    },
    currentUsername() {
      return classificationStore.state.currentUsername;
    },
    enabledSend() {
      return this.comment && (this.comment.attachments && this.comment.attachments.length || htmlValidator(this.comment.content))
    },
    autoCompress() {
      return 500 * 1024;
    }
  },
  methods: {
    async send() {
      if (this.enabledSend) {
        this.submitting = true;
        this.$emit('submitting', { id: this.id, isSubmitting: true });
        /* locus id */
        this.comment.locusId = this.locusId;
        /* locus detail id */
        if (this.locusDetailId) 
          this.comment.locusDetailId = parseInt(this.locusDetailId);
        /* locus collaboration id */
        if (this.locusCollaborationId) 
          this.comment.locusCollaborationId = parseInt(this.locusCollaborationId);

        try {
          let res = await classificationStore.dispatch("sendComment", this.comment);
          /* reset */
          this.$emit('updateComment', {});
          this.editor.clearContent();
          /* $emit */
          this.$emit("added", res.data);
        } catch (err) {
          console.error(err);
          this.$toasted.global.error({
            response: err.response,
          });
        }
        this.submitting = false;
        this.$emit('submitting', { id: this.id, isSubmitting: false });
      }
    },
    emojiOnUpdated(item) {
      const transaction = this.editor.state.tr.insertText(item);
      this.editor.view.dispatch(transaction);
      this.editor.focus();
    },
    // navigate to the previous item
    // if it's the first item, navigate to the last one
    upHandler() {
      this.navigatedUserIndex =
        (this.navigatedUserIndex + this.filteredUsers.length - 1) %
        this.filteredUsers.length;
    },
    // navigate to the next item
    // if it's the last item, navigate to the first one
    downHandler() {
      this.navigatedUserIndex =
        (this.navigatedUserIndex + 1) % this.filteredUsers.length;
    },
    enterHandler() {
      const user = this.filteredUsers[this.navigatedUserIndex];
      if (user) {
        this.selectUser(user);
      }
    },
    // we have to replace our suggestion text with a mention
    // so it's important to pass also the position of your suggestion text
    selectUser(user) {
      this.insertMention({
        range: this.suggestionRange,
        attrs: {
          id: user.userName || user.username,
          label: user.userName || user.username,
        },
      });
      this.editor.focus();
    },
    // renders a popup with suggestions
    // tiptap provides a virtualNode object for using popper.js (or tippy.js) for popups
    renderPopup(node) {
      if (this.popup) {
        return;
      }
      this.popup = tippy(node, {
        content: this.$refs.suggestions,
        trigger: "mouseenter",
        interactive: true,
        theme: "dark",
        placement: "top-start",
        inertia: true,
        duration: [400, 200],
        showOnInit: true,
        arrow: true,
        arrowType: "round",
      });
      // we have to update tippy whenever the DOM is updated
      if (MutationObserver) {
        this.observer = new MutationObserver(() => {
          this.popup.popperInstance.scheduleUpdate();
        });
        this.observer.observe(this.$refs.suggestions, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
    },
    destroyPopup() {
      if (this.popup) {
        this.popup.destroy();
        this.popup = null;
      }
      if (this.observer) {
        this.observer.disconnect();
      }
    },
    //upload
    onPaste() {
      let vm = this;
      var items = (event.clipboardData || event.originalEvent.clipboardData).items;

      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        if (item.kind === "file") {
          //item.name = "test.png";
          //vm.$refs.addDataTransfer(file);
          var file = item.getAsFile();
          //file.name = 'test.png'
          vm.$refs.upload.add(file, { active: false });
        }
      }
    },
    onFocus() {
      this.$nextTick(() => {
        this.editor.focus();
        this.$el.scrollIntoView(this.scrollIntoViewOptions);
      });
    },
    /**
     * Has changed
     * @param  Object|undefined   newFile   只读
     * @param  Object|undefined   oldFile   只读
     * @return undefined
     */
    inputFile(newFile, oldFile) {
      if (newFile && oldFile) {
        /* before send */
        if (newFile.active && !oldFile.active) {
          if (newFile.name === "image.png" || newFile.name === "unknown")
            newFile.name = `${commons.nanoID()}.png`;
          else
            newFile.name = newFile.name.replaceAll("/", "_");
        }
        /* on progress */
        if (newFile.progress !== oldFile.progress) {
          // progress
        }
        /* on error */
        if (newFile.error && !oldFile.error) {
          if (newFile.xhr) {
            /* xhr status */
            console.log("status", newFile.xhr.status);
            /* xhr error handling */
            if (newFile.xhr.status === 413)
              this.$toasted.error('Upload failed. Request entity too large.');
            else if (newFile.xhr.status === 500)
              this.$toasted.error('Upload failed. Something went wrong.');
          } else if (newFile.error == "size") {
            /* size error handling */
            this.$toasted.error(`Upload failed. File size can not exceed ${this.item.maxFileSize} MB.`);
          }
        }
        /* on success */
        if (newFile.success && !oldFile.success) {
          let attachments = [];
          try {
            attachments = [...this.comment.attachments];
          } catch (err) { 
            //
          }
          attachments.push({
            path: newFile.response.url,
            name: newFile.response.filename,
            size: newFile.response.size
          });
          this.$set(this.comment, 'attachments', attachments);
        }
        /* remove file from queue */
        if (!newFile.active && oldFile.active) {
          this.removeQueueFile(newFile);
        }
      }
      /* remove */
      if (!newFile && oldFile) {
        if (oldFile.success && oldFile.response.id) {
          // delete
        }
      }
      /* automatically activate upload */
      if (
        Boolean(newFile) !== Boolean(oldFile) ||
        oldFile.error !== newFile.error
      ) {
        if (!this.$refs.upload.active) {
          this.$refs.upload.active = true;
        }
      }
    },
    /**
     * Pretreatment
     * @param  Object|undefined   newFile   读写
     * @param  Object|undefined   oldFile   只读
     * @param  Function           prevent   阻止回调
     * @return undefined
     */
    inputFilter(newFile, oldFile) {
      if (
        newFile
        && !oldFile
        && commons.isImageFile(newFile.name)
      ) {
        // Automatic compression
        // 自动压缩
        if (
          newFile.file &&
          newFile.type.substr(0, 6) === "image/" &&
          this.autoCompress > 0 &&
          this.autoCompress < newFile.size
        ) {
          let vm = this;

          newFile.error = "compressing";
          const imageCompressor = new ImageCompressor(newFile.file, {
            maxWidth: 1200,
            maxHeight: 1200,
            success(file) {
              vm.$refs.upload.update(newFile, {
                error: "",
                file,
                size: file.size,
                type: file.type,
              });
            },
            error(err) {
              vm.$refs.upload.update(newFile, {
                error: err.message || "compress",
              });
            },
          });
        }
      }

      if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
        if (newFile.file != null) {
          // Create a blob field
          // 创建 blob 字段
          newFile.blob = "";
          let URL = window.URL || window.webkitURL;
          if (URL && URL.createObjectURL) {
            newFile.blob = URL.createObjectURL(newFile.file);
          }

          newFile.thumb = "";
          if (newFile.blob && newFile.type.substr(0, 6) === "image/") {
            newFile.thumb = newFile.blob;
          }
        } else {
          var reader = new FileReader();
          reader.onload = function (event) {
            newFile.blob = event.target.result;
            newFile.thumb = "";
            if (newFile.blob && newFile.type.substr(0, 6) === "image/") {
              newFile.thumb = newFile.blob;
            }
          }; // data url!
          reader.readAsDataURL(newFile);
        }
        // Thumbnails
        // 缩略图
      }
    },
    removeFile(file) {
      this.comment.attachments.splice(this.comment.attachments.indexOf(file), 1);
    },
    removeQueueFile(file) {
      const fileIndex = this.files.indexOf(file);
      if (fileIndex !== -1)
        this.files.splice(fileIndex, 1);
    },
    cancelUpload(file) {
      if (file.active)
        this.$refs.upload.active = false;
      else
        this.removeQueueFile(file);
    },
    insertTag() {
      const transaction = this.editor.state.tr.insertText(" @");
      this.editor.view.dispatch(transaction);
      this.editor.focus();
    },
  },
  watch: {
    'comment.content': {
      handler: function (val, oldVal) {
        this.$nextTick(() => {  
          if (this.editor) {
            if (oldVal && !val) {
              this.editor.clearContent();
            } else {
              const state = this.editor.getHTML();
              const content = getCommentData(state);
              if (val !== content) {
                this.editor.setContent(val, false, {preserveWhitespace: true});
              }
            }
          }
        });
      },
      deep: true
    },
    showComment(val) {
      if (val) {
        let vm = this;
        let editorExtensions = [
          new Doc(),
          new HardBreak(),
          new Placeholder({
            emptyEditorClass: 'is-editor-empty',
            emptyNodeClass: 'is-empty',
            showOnlyWhenEditable: true,
            showOnlyCurrent: true,
            emptyNodeText: (node) => {
              return vm.placeholder;
            },
          }),
          new Bold(),
          new Italic(),
          new History(),
        ];

        if (this.mention) {
          editorExtensions.push(
            new Mention({
              // a list of all suggested items
              items: () => this.users,
              // is called when a suggestion starts
              onEnter: ({ items, query, range, command, virtualNode }) => {
                this.query = query;
                this.filteredUsers = items;
                this.suggestionRange = range;
                this.renderPopup(virtualNode);
                // we save the command for inserting a selected mention
                // this allows us to call it inside of our custom popup
                // via keyboard navigation and on click
                this.insertMention = command;
              },
              // is called when a suggestion has changed
              onChange: ({ items, query, range, virtualNode }) => {
                this.query = query;
                this.filteredUsers = items;
                this.suggestionRange = range;
                this.navigatedUserIndex = 0;
                this.renderPopup(virtualNode);
              },
              // is called when a suggestion is cancelled
              onExit: () => {
                // reset all saved values
                this.query = null;
                this.filteredUsers = [];
                this.suggestionRange = null;
                this.navigatedUserIndex = 0;
                this.destroyPopup();
              },
              // is called on every keyDown event while a suggestion is active
              onKeyDown: ({ event }) => {
                // pressing up arrow
                if (event.keyCode === 38) {
                  this.upHandler();
                  return true;
                }
                // pressing down arrow
                if (event.keyCode === 40) {
                  this.downHandler();
                  return true;
                }
                // pressing enter
                if (event.keyCode === 13) {
                  this.enterHandler();
                  return true;
                }
                return false;
              },
              // is called when a suggestion has changed
              // this function is optional because there is basic filtering built-in
              // you can overwrite it if you prefer your own filtering
              // in this example we use fuse.js with support for fuzzy search
              onFilter: (items, query) => {
                if (query) {
                  let hardbreak = query.indexOf('\u0000');
                  if (hardbreak > -1) {
                    query = query.substring(hardbreak, query.length - hardbreak);
                  }
                  return items
                    .filter((x) =>
                      (
                        x.displayName.toLowerCase().indexOf(query.toLowerCase()) > -1 ||
                        x.username.toLowerCase().indexOf(query.toLowerCase()) > -1
                      ) && !x.disable
                    )
                    .slice(0, 10);
                }
                return items.slice(0, 10);
              },
            })
          );
        }

        this.editor = new Editor({
          onUpdate: ({ getHTML }) => {
            const state = getHTML();
            const content = getCommentData(state);
            vm.$set(vm.comment, 'content', content);
            vm.$el.scrollIntoView(vm.scrollIntoViewOptions);
          },
          extensions: editorExtensions,
          content: this.comment.content,
        });
        this.editor.setContent(Boolean(this.comment.content) ? this.comment.content : "<p></p>");
      } else {
        this.editor && this.editor.destroy();
      }
    }
  },
  mounted() {
    this.id = this._uid;
    this.initialComment && (this.showComment = true);
  },
  beforeDestroy() {
    this.destroyPopup();
    this.editor && this.editor.destroy();
  }
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

$uploaderColor: rgba(57, 81, 155, 0.2) !default;

.fts-comment {
  .comment-toggle {
    padding: 0.5rem 0.75rem;
    border: 1px solid transparent;
    border-radius: $border-radius;
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    color: rgba(var(--fts-gray-400-rgb), 0.75);
    cursor: pointer;
    &:hover {
      background-color: rgba(var(--fts-gray-100-rgb), 0.75);
    }
  }
  .comment-file-attachments {
    margin-left: 1px;
    margin-bottom: .375rem;
  }
  .comment-wrapper {
    display: flex;
    align-items: flex-start;
    .comment-user-profile {
      margin-right: 0.75rem;
    }
    .comment-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      border: 1px solid var(--fts-form-control-border);
      border-radius: $border-radius;
      background-color: var(--fts-card-background);
      .comment-uploader {
        padding: .375rem .375rem 0;
      }
      .comment-editor {
        flex: 1;
        padding: 0.5rem 0.75rem;
        font-size: var(--inistate-label-text-14);
        line-height: var(--inistate-paragraph-text-14--line-height);
        overflow: auto;
        & *.is-empty:nth-child(1)::before {
          content: attr(data-empty-text);
          float: left;
          color: rgba(var(--fts-gray-400-rgb), 0.75);
          pointer-events: none;
          height: 0;
        }
        .editor__content {
          word-break: break-word;
          color: var(--fts-form-control);
        }
      }
      .comment-action-bar {
        min-height: 2.25rem;
        display: flex;
        align-items: center;
        padding-left: 0.75rem;
        padding-right: 0.75rem;
        border-top: 1px solid transparent;
        font-size: var(--inistate-label-text-14);
        line-height: var(--inistate-paragraph-text-14--line-height);
        .comment-actions {
          flex: 1;
          display: flex;
          align-items: center;
          > div:not(:first-child) {
            margin-left: 1rem;
          }
          .file-upload-container {
            display: flex;
            &.icon-handle {
              label {
                cursor: pointer;
              }
            }
            &:hover {
              .comment-action-icon {
                color: var(--fts-primary);
              }
            }
          }
          .comment-action-icon {
            color: var(--fts-gray-400);
            &:not(.disabled):hover {
              color: var(--fts-primary);
              cursor: pointer;
            }
            &.disabled {
              cursor: not-allowed;
            }
          }
        }
      }
    }
  }
}

.mention {
  @include text-overflow-ellipsis;
  max-width: 100%;
  display: inline-flex;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
  border-radius: $border-radius;
  background-color: var(--fts-gray-100);
  color: var(--fts-text);
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-paragraph-text-14--line-height);
}
.mention-suggestion {
  color: var(--fts-text);
}

.suggestion-list {
  padding: 0.2rem;
  .suggestion-list__item {
    display: flex;
    align-items: center;
    margin-bottom: 0.2rem;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    cursor: pointer;
    &:last-child {
      margin-bottom: 0;
    }
    &.is-empty {
      opacity: 0.5;
    }
    &:hover,
    &.is-selected {
      @include theme() {
        background-color: darken(theme-get('fts-popover-background'), 5%);
      }
    }
    .profile-name {
      color: inherit;
      .user-profile-name {
        font-size: 0.75rem;
        font-weight: 500;
      }
      .user-profile-username {
        font-size: 0.675rem;
        opacity: 0.75;
      }
    }
  }
}
.suggestion-list__no-results {
  padding: 0.2rem 0.5rem;
}

.tippy-tooltip.dark-theme {
  padding: 0; 
  border-radius: 0.25rem;
  background-color: var(--fts-popover-background);
  color: var(--fts-light);
  font-size: 1rem;
  text-align: inherit;
  .tippy-backdrop {
    display: none;
  }
  .tippy-roundarrow {
    fill: var(--fts-popover-background);
  }
}
.tippy-popper[x-placement^="top"] .tippy-tooltip.dark-theme .tippy-arrow {
  border-top-color: var(--fts-popover-background);
}
.tippy-popper[x-placement^="bottom"] .tippy-tooltip.dark-theme .tippy-arrow {
  border-bottom-color: var(--fts-popover-background);
}
.tippy-popper[x-placement^="left"] .tippy-tooltip.dark-theme .tippy-arrow {
  border-left-color: var(--fts-popover-background);
}
.tippy-popper[x-placement^="right"] .tippy-tooltip.dark-theme .tippy-arrow {
  border-right-color: var(--fts-popover-background);
}
</style>